import Fuse from "fuse.js";
import jsonCities from "~/data/cities.json";
import emailProviders from "~/data/email_providers.json";
import type { IValue } from "~/interfaces";

export function genericSearch(searchTerm: string, values?: IValue[]): string[] {
   const elementsToSearch = new Fuse(values, {
      keys: ["label"],
      threshold: 0.0,
      location: 0,
      distance: 0,
      minMatchCharLength: 0,
      isCaseSensitive: false,
   });

   if (!searchTerm.trim()) {
      return [];
   }

   if (searchTerm.length < 2) {
      return [];
   }

   return elementsToSearch.search(searchTerm).map((result: any) => result.item.label);
}

export function searchCity(searchTerm: string): string[] {
   const values: IValue[] = jsonCities.map((city) => ({
      value: city.Prov,
      label: `${city.Citta} (${city.Prov})`,
   }));

   return genericSearch(searchTerm, values);
}

export function searchEmail(searchTerm: string): string[] {
   if (!searchTerm.trim()) {
      return [];
   }

   const values: IValue[] = emailProviders.map((provider) => ({
      value: `${provider.domain}.${provider.extension}`,
      label: `${provider.domain}.${provider.extension}`,
   }));

   if (!searchTerm.includes("@")) {
      return [];
   }

   const [name, domainWithExtension] = searchTerm.split("@");

   if (!domainWithExtension) {

      return values.map((result: IValue) => `${name}@${result.value}`);
   }

   const [domainPart, extensionPart] = (() => {
      const lastDotIndex = domainWithExtension.lastIndexOf(".");
      return lastDotIndex !== -1
         ? [domainWithExtension.slice(0, lastDotIndex), domainWithExtension.slice(lastDotIndex + 1)]
         : [domainWithExtension, ""];
   })();

   const domain = domainPart.includes(".") ? domainPart.split(".")[0] : domainPart;

   if (!domain) {
      return values.map((result: IValue) => `${name}@${result.value}`);
   }

   if (domain && !extensionPart) {

      const domainCorrections = emailProviders.map((provider) => provider.domain);

      const fuse = new Fuse(domainCorrections, {
         threshold: 0.0,
      });

      const correctedDomain = fuse.search(domain)?.[0]?.item || domain;

      return values
         .filter((value) => value.value.startsWith(correctedDomain))
         .map((value) => `${name}@${value.value}`);
   }

   if (domain && extensionPart) {
      const domainCorrections = emailProviders.map((provider) => provider.domain);
      const fuse = new Fuse(domainCorrections, {
         threshold: 0.0,
      });

      const correctedDomain = fuse.search(domain)?.[0]?.item || domain;

      const extensionMatches = emailProviders
         .filter((provider) => provider.domain === correctedDomain)
         .flatMap((provider) => {
            const correctExtension = provider.extension;
            return extensionPart !== correctExtension
               ? [`${name}@${correctedDomain}.${correctExtension}`]
               : [];
         });

      return Array.from(new Set(extensionMatches));
   }

   return genericSearch(searchTerm, values);
}